/**
 * Student-housing Employment extras:
 * - Work Authorization (+ expiration for visas)
 * - Student financial assistance Yes/No + multi-row details
 * - Parental / other income Yes/No + details
 */
import { format } from 'date-fns'
import { Plus, X } from 'lucide-react'
import { useRef, useState } from 'react'
import {
	FloatingLabelField,
	FloatingSelectField,
} from '#app/components/forms'
import { AdditionalYesNoRow } from '#app/components/screening/additional-question-ui'
import { Button } from '#app/components/ui/button'
import { MonthYearPicker } from '../MonthYearPicker'

export const WORK_AUTHORIZATION_OPTIONS = [
	'Canadian Citizen',
	'Permanent Resident',
	'Refugee',
	'Student Visa',
	'Visitor Visa',
	'Work Visa',
] as const

export const STUDENT_ASSISTANCE_TYPES = [
	'Scholarship',
	'Grant',
	'Loan',
	'Other',
] as const

export const ASSISTANCE_FREQUENCIES = [
	'Weekly',
	'Monthly',
	'Yearly',
] as const

export const PARENTAL_ASSISTANCE_SOURCES = [
	'Parental Assistance',
	'Other Family Support',
	'Side Income / Freelance',
	'Other',
] as const

const VISA_STATUSES = ['Student Visa', 'Work Visa', 'Visitor Visa'] as const

/** Citizen / PR → show the Canadian Bank Account popup on Employment Next. */
export const WORK_AUTH_TRIGGERS_BANK_PROMPT = [
	'Canadian Citizen',
	'Permanent Resident',
] as const

/** @deprecated Use WORK_AUTH_TRIGGERS_BANK_PROMPT — kept for older imports. */
export const WORK_AUTH_EXEMPT_FROM_BANK_PROMPT = WORK_AUTH_TRIGGERS_BANK_PROMPT

/**
 * Student Employment Next: show the Canadian bank account popup when work
 * authorization is Canadian Citizen or Permanent Resident.
 * Yes → Verify Income; No → skip Verify Income to Review.
 * Other statuses follow the normal flow (no popup).
 */
export function needsCanadianBankAccountPrompt(
	immigrationStatus: string,
): boolean {
	const status = immigrationStatus.trim()
	if (!status) return false
	return (WORK_AUTH_TRIGGERS_BANK_PROMPT as readonly string[]).includes(status)
}

export type StudentFinancialAssistanceEntry = {
	id: string
	type: string
	grossIncome: string
	frequency: string
}

export type StudentEmploymentAnswers = {
	immigrationStatus: string
	immigrationStatusExpiration: string
	receivingStudentFinancialAssistance: string
	studentFinancialAssistance: StudentFinancialAssistanceEntry[]
	receivingParentalAssistance: string
	parentalAssistanceSource: string
	parentalAssistanceGrossAmount: string
	parentalAssistanceFrequency: string
}

/** Flat + dotted keys (e.g. studentFinancialAssistance.0.type). */
export type StudentEmploymentErrors = Partial<Record<string, string[]>>

export function emptyStudentFinancialAssistanceEntry(): StudentFinancialAssistanceEntry {
	return {
		id: crypto.randomUUID(),
		type: '',
		grossIncome: '',
		frequency: '',
	}
}

export const emptyStudentEmploymentAnswers = (): StudentEmploymentAnswers => ({
	immigrationStatus: '',
	immigrationStatusExpiration: '',
	receivingStudentFinancialAssistance: '',
	studentFinancialAssistance: [],
	receivingParentalAssistance: '',
	parentalAssistanceSource: '',
	parentalAssistanceGrossAmount: '',
	parentalAssistanceFrequency: '',
})

/** Normalize saved array / JSON / legacy single-row fields into entries. */
export function parseStudentFinancialAssistance(
	source: {
		studentFinancialAssistance?: unknown
		studentAssistanceType?: string
		studentAssistanceGrossIncome?: string
		studentAssistanceFrequency?: string
	},
): StudentFinancialAssistanceEntry[] {
	const raw = source.studentFinancialAssistance
	let list: unknown[] = []
	if (Array.isArray(raw)) {
		list = raw
	} else if (typeof raw === 'string' && raw.trim()) {
		try {
			const parsed = JSON.parse(raw) as unknown
			if (Array.isArray(parsed)) list = parsed
		} catch {
			list = []
		}
	}

	const fromArray = list
		.map((item) => {
			if (!item || typeof item !== 'object') return null
			const row = item as Record<string, unknown>
			const type = String(row.type ?? row.studentAssistanceType ?? '')
			const grossIncome = String(
				row.grossIncome ?? row.studentAssistanceGrossIncome ?? '',
			)
			const frequency = String(
				row.frequency ?? row.studentAssistanceFrequency ?? '',
			)
			const id =
				typeof row.id === 'string' && row.id
					? row.id
					: crypto.randomUUID()
			return { id, type, grossIncome, frequency }
		})
		.filter((row): row is StudentFinancialAssistanceEntry => row !== null)

	if (fromArray.length > 0) return fromArray

	const legacyType = source.studentAssistanceType || ''
	const legacyIncome = source.studentAssistanceGrossIncome || ''
	const legacyFrequency = source.studentAssistanceFrequency || ''
	if (legacyType || legacyIncome || legacyFrequency) {
		return [
			{
				id: crypto.randomUUID(),
				type: legacyType,
				grossIncome: legacyIncome,
				frequency: legacyFrequency,
			},
		]
	}

	return []
}

export function pickStudentEmploymentAnswers(
	source: Partial<
		Omit<StudentEmploymentAnswers, 'studentFinancialAssistance'>
	> & {
		studentFinancialAssistance?: unknown
		studentAssistanceType?: string
		studentAssistanceGrossIncome?: string
		studentAssistanceFrequency?: string
	},
): StudentEmploymentAnswers {
	return {
		immigrationStatus: source.immigrationStatus || '',
		immigrationStatusExpiration: source.immigrationStatusExpiration || '',
		receivingStudentFinancialAssistance:
			source.receivingStudentFinancialAssistance || '',
		studentFinancialAssistance: (() => {
			const parsed = parseStudentFinancialAssistance(source)
			if (
				(source.receivingStudentFinancialAssistance || '') === 'yes' &&
				parsed.length === 0
			) {
				return [emptyStudentFinancialAssistanceEntry()]
			}
			return parsed
		})(),
		receivingParentalAssistance: source.receivingParentalAssistance || '',
		parentalAssistanceSource: source.parentalAssistanceSource || '',
		parentalAssistanceGrossAmount: source.parentalAssistanceGrossAmount || '',
		parentalAssistanceFrequency: source.parentalAssistanceFrequency || '',
	}
}

export function validateStudentEmploymentAnswers(
	answers: StudentEmploymentAnswers,
	enabledKeys?: readonly string[],
): StudentEmploymentErrors {
	const on = (key: string) => !enabledKeys || enabledKeys.includes(key)
	const errors: StudentEmploymentErrors = {}

	if (!answers.immigrationStatus.trim()) {
		errors.immigrationStatus = ['Work authorization status is required']
	} else if (
		(VISA_STATUSES as readonly string[]).includes(answers.immigrationStatus) &&
		!answers.immigrationStatusExpiration.trim()
	) {
		errors.immigrationStatusExpiration = ['Expiration Date is required']
	}

	if (on('student_financial_assistance')) {
		if (!answers.receivingStudentFinancialAssistance) {
			errors.receivingStudentFinancialAssistance = [
				'Please answer this question',
			]
		} else if (answers.receivingStudentFinancialAssistance === 'yes') {
			const rows = answers.studentFinancialAssistance
			if (rows.length === 0) {
				errors.studentFinancialAssistance = [
					'Add at least one financial assistance entry',
				]
			}
			for (const [index, row] of rows.entries()) {
				if (!row.type.trim()) {
					errors[`studentFinancialAssistance.${index}.type`] = [
						'Type is required',
					]
				}
				if (!row.grossIncome.trim()) {
					errors[`studentFinancialAssistance.${index}.grossIncome`] = [
						'Gross income is required',
					]
				}
				if (!row.frequency.trim()) {
					errors[`studentFinancialAssistance.${index}.frequency`] = [
						'Frequency is required',
					]
				}
			}
		}
	}

	if (on('parental_assistance')) {
		if (!answers.receivingParentalAssistance) {
			errors.receivingParentalAssistance = ['Please answer this question']
		} else if (answers.receivingParentalAssistance === 'yes') {
			if (!answers.parentalAssistanceSource.trim()) {
				errors.parentalAssistanceSource = ['Source is required']
			}
			if (!answers.parentalAssistanceGrossAmount.trim()) {
				errors.parentalAssistanceGrossAmount = ['Gross amount is required']
			}
			if (!answers.parentalAssistanceFrequency.trim()) {
				errors.parentalAssistanceFrequency = ['Frequency is required']
			}
		}
	}

	return errors
}

export function appendStudentEmploymentFormData(
	submitData: FormData,
	answers: StudentEmploymentAnswers,
) {
	submitData.append('immigrationStatus', answers.immigrationStatus)
	submitData.append(
		'immigrationStatusExpiration',
		answers.immigrationStatusExpiration,
	)
	submitData.append(
		'receivingStudentFinancialAssistance',
		answers.receivingStudentFinancialAssistance,
	)
	const studentYes = answers.receivingStudentFinancialAssistance === 'yes'
	const rows = studentYes ? answers.studentFinancialAssistance : []
	submitData.append('studentFinancialAssistance', JSON.stringify(rows))
	// Mirror first row into legacy keys for older readers / reports.
	const first = rows[0]
	submitData.append('studentAssistanceType', first?.type ?? '')
	submitData.append('studentAssistanceGrossIncome', first?.grossIncome ?? '')
	submitData.append('studentAssistanceFrequency', first?.frequency ?? '')
	submitData.append(
		'receivingParentalAssistance',
		answers.receivingParentalAssistance,
	)
	const parentalYes = answers.receivingParentalAssistance === 'yes'
	submitData.append(
		'parentalAssistanceSource',
		parentalYes ? answers.parentalAssistanceSource : '',
	)
	submitData.append(
		'parentalAssistanceGrossAmount',
		parentalYes ? answers.parentalAssistanceGrossAmount : '',
	)
	submitData.append(
		'parentalAssistanceFrequency',
		parentalYes ? answers.parentalAssistanceFrequency : '',
	)
}

export function WorkAuthorizationFields({
	values,
	errors,
	onChange,
	/** When true, render fields only (no outer grid) so they sit beside Employment Type. */
	inline = false,
}: {
	values: Pick<
		StudentEmploymentAnswers,
		'immigrationStatus' | 'immigrationStatusExpiration'
	>
	errors?: Pick<
		StudentEmploymentErrors,
		'immigrationStatus' | 'immigrationStatusExpiration'
	>
	onChange: (
		field: 'immigrationStatus' | 'immigrationStatusExpiration',
		value: string,
	) => void
	inline?: boolean
}) {
	const expirationRef = useRef<HTMLDivElement>(null)
	const [openExpiration, setOpenExpiration] = useState(false)
	const expirationDate = values.immigrationStatusExpiration
		? new Date(values.immigrationStatusExpiration)
		: undefined
	const validExpiration =
		expirationDate && !Number.isNaN(expirationDate.getTime())
			? expirationDate
			: undefined

	const showExpiration = (VISA_STATUSES as readonly string[]).includes(
		values.immigrationStatus,
	)

	const fields = (
		<>
			<FloatingSelectField
				labelProps={{
					children: <span>Work Authorization Status</span>,
					className: 'text-[13px] md:text-[16px] text-[#32302F]',
				}}
				value={values.immigrationStatus}
				onChange={(value) => {
					onChange('immigrationStatus', value)
					if (!(VISA_STATUSES as readonly string[]).includes(value)) {
						onChange('immigrationStatusExpiration', '')
					}
				}}
				options={WORK_AUTHORIZATION_OPTIONS.map((method) => ({
					label: method,
					value: method,
				}))}
				errors={errors?.immigrationStatus}
				errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
				placeholder="Select Work Authorization Status"
			/>

			{showExpiration ? (
				<div ref={expirationRef} className="relative">
					<FloatingLabelField
						inputProps={{
							type: 'text',
							readOnly: true,
							placeholder: 'Select Expiration Date',
							className: `cursor-pointer placeholder:text-[#CCCCCC] placeholder:text-[16px] placeholder:text-regular text-[16px] px-[33px] py-[24px] text-[#484848] border rounded-[15px] bg-[#FBFDFD] ${
								errors?.immigrationStatusExpiration?.length
									? 'border-[#B33044]!'
									: 'border-[#CCCCCC]'
							}`,
							value: validExpiration
								? format(validExpiration, 'MMMM yyyy')
								: '',
							onClick: () => setOpenExpiration(true),
						}}
						labelProps={{
							children: <span>Expiration Date</span>,
							className: 'text-[13px] md:text-[16px] block ',
						}}
						errors={errors?.immigrationStatusExpiration}
						errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
						rightIcon={
							<img
								src="/img/calender.png"
								alt="calendar"
								className="h-6 w-6"
							/>
						}
					/>
					{openExpiration ? (
						<MonthYearPicker
							title="Expiration Date"
							value={validExpiration}
							onChange={(date) => {
								onChange(
									'immigrationStatusExpiration',
									date ? format(date, 'yyyy-MM-dd') : '',
								)
								setOpenExpiration(false)
							}}
							triggerRef={expirationRef}
							showFlexibleToggle={false}
						/>
					) : null}
				</div>
			) : (
				<div />
			)}
		</>
	)

	if (inline) return fields

	return (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-3">{fields}</div>
	)
}

/** Student + parental assistance questions (after government assistance). */
export function StudentAssistanceQuestions({
	namePrefix,
	values,
	errors,
	onChange,
	enabledKeys,
	/** When true, render rows only — parent supplies the outer Additional Questions card. */
	embedded = false,
}: {
	namePrefix: string
	values: StudentEmploymentAnswers
	errors?: StudentEmploymentErrors
	onChange: (
		field: keyof StudentEmploymentAnswers,
		value: string | StudentFinancialAssistanceEntry[],
	) => void
	/** Catalog prebuilt keys; omit to show all (legacy). */
	enabledKeys?: readonly string[]
	embedded?: boolean
}) {
	const on = (key: string) => !enabledKeys || enabledKeys.includes(key)
	const showStudentFinancial = on('student_financial_assistance')
	const showParental = on('parental_assistance')
	if (!showStudentFinancial && !showParental) return null

	const rows = values.studentFinancialAssistance

	const updateRow = (
		index: number,
		field: keyof Omit<StudentFinancialAssistanceEntry, 'id'>,
		value: string,
	) => {
		const next = rows.map((row, i) =>
			i === index ? { ...row, [field]: value } : row,
		)
		onChange('studentFinancialAssistance', next)
	}

	const addRow = () => {
		onChange('studentFinancialAssistance', [
			...rows,
			emptyStudentFinancialAssistanceEntry(),
		])
	}

	const removeRow = (index: number) => {
		if (rows.length <= 1) {
			onChange('studentFinancialAssistance', [
				emptyStudentFinancialAssistanceEntry(),
			])
			return
		}
		onChange(
			'studentFinancialAssistance',
			rows.filter((_, i) => i !== index),
		)
	}

	return (
		<div className={embedded ? 'space-y-4 sm:space-y-6' : 'space-y-6'}>
			{showStudentFinancial ? (
			<div className="space-y-3">
				<AdditionalYesNoRow
					name={`${namePrefix}-student-financial`}
					label="Are You Receiving Student Financial Assistance (Scholarships, Grants, Loans)?"
					value={values.receivingStudentFinancialAssistance}
					error={errors?.receivingStudentFinancialAssistance?.[0]}
					onChange={(v) => {
						onChange('receivingStudentFinancialAssistance', v)
						// Keep existing rows when toggling (demo prefill / re-open Yes).
						// Only seed a blank row when Yes and nothing is stored yet.
						if (
							v === 'yes' &&
							values.studentFinancialAssistance.length === 0
						) {
							onChange('studentFinancialAssistance', [
								emptyStudentFinancialAssistanceEntry(),
							])
						}
					}}
				/>

				{values.receivingStudentFinancialAssistance === 'yes' ? (
					<div className="rounded-[35px] border border-[#F0F0F0] p-5 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
						<h3 className="mb-4 font-sans text-[20px] font-medium leading-[26px] text-[#747474]">
							Financial Assistance
						</h3>
						<div className="space-y-4">
							{rows.map((row, index) => {
								const typeErr =
									errors?.[`studentFinancialAssistance.${index}.type`]
								const incomeErr =
									errors?.[
										`studentFinancialAssistance.${index}.grossIncome`
									]
								const freqErr =
									errors?.[
										`studentFinancialAssistance.${index}.frequency`
									]
								return (
									<div
										key={row.id}
										className={
											rows.length > 1
												? 'grid grid-cols-1 items-start gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]'
												: 'grid grid-cols-1 gap-4 md:grid-cols-3'
										}
									>
										<FloatingSelectField
											labelProps={{
												children: <>Type</>,
												className:
													'text-[14px] md:text-[16px] text-[#32302F]',
											}}
											value={row.type}
											onChange={(v) => updateRow(index, 'type', v)}
											options={STUDENT_ASSISTANCE_TYPES.map((t) => ({
												label: t,
												value: t,
											}))}
											errors={typeErr}
											errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
											placeholder="Select Type"
										/>
										<FloatingLabelField
											currency
											labelProps={{
												children: <>Gross Income</>,
												htmlFor: `${namePrefix}-student-gross-${row.id}`,
												className:
													'text-[14px] md:text-[16px] text-[#32302F]',
											}}
											inputProps={{
												id: `${namePrefix}-student-gross-${row.id}`,
												value: row.grossIncome,
												placeholder: 'Enter Gross Income',
												onChange: (e) =>
													updateRow(index, 'grossIncome', e.target.value),
												className: `placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border rounded-[15px] bg-[#FBFDFD] ${
													incomeErr?.length
														? 'border-[#B33044]!'
														: 'border-[#CCCCCC]'
												}`,
											}}
											errors={incomeErr}
											errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
										/>
										<FloatingSelectField
											labelProps={{
												children: <>Frequency</>,
												className:
													'text-[14px] md:text-[16px] text-[#32302F]',
											}}
											value={row.frequency}
											onChange={(v) => updateRow(index, 'frequency', v)}
											options={ASSISTANCE_FREQUENCIES.map((t) => ({
												label: t,
												value: t,
											}))}
											errors={freqErr}
											errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
											placeholder="Select Frequency"
										/>
										{rows.length > 1 ? (
											<button
												type="button"
												onClick={() => removeRow(index)}
												className="flex h-[56px] w-9 shrink-0 cursor-pointer items-center justify-center justify-self-start text-[#A8A8A8] hover:text-[#B33044]"
												aria-label="Remove financial assistance row"
											>
												<X className="h-5 w-5" strokeWidth={1.75} />
											</button>
										) : null}
									</div>
								)
							})}
							{errors?.studentFinancialAssistance?.[0] ? (
								<p className="text-[12px] font-[400] text-[#be123d]">
									{errors.studentFinancialAssistance[0]}
								</p>
							) : null}
							<Button
								type="button"
								variant="outline"
								onClick={addRow}
								className="h-auto cursor-pointer rounded-[15px] border-[#E8E8E8] px-4 py-2.5 text-[14px] font-medium text-[#32302F] hover:bg-gray-50"
							>
								<Plus className="mr-1.5 h-4 w-4" />
								Add Another
							</Button>
						</div>
					</div>
				) : null}
			</div>
			) : null}

			{showParental ? (
			<div className="space-y-3">
				<AdditionalYesNoRow
					name={`${namePrefix}-parental`}
					label="Are You Receiving Parental Assistance or Any Other Type of Income?"
					value={values.receivingParentalAssistance}
					error={errors?.receivingParentalAssistance?.[0]}
					onChange={(v) => onChange('receivingParentalAssistance', v)}
				/>

				{values.receivingParentalAssistance === 'yes' ? (
					<div className="rounded-[35px] p-5 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
						<h3 className="mb-4 font-sans text-[20px] font-medium leading-[26px] text-[#747474]">
							Parental Assistance / Other Income
						</h3>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
							<FloatingSelectField
								labelProps={{
									children: <>Source</>,
									className: 'text-[14px] md:text-[16px] text-[#32302F]',
								}}
								value={values.parentalAssistanceSource}
								onChange={(v) => onChange('parentalAssistanceSource', v)}
								options={PARENTAL_ASSISTANCE_SOURCES.map((t) => ({
									label: t,
									value: t,
								}))}
								errors={errors?.parentalAssistanceSource}
								errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
								placeholder="Select Source"
							/>
							<FloatingLabelField
								currency
								labelProps={{
									children: <>Gross Amount (CAD)</>,
									htmlFor: `${namePrefix}-parental-gross`,
									className: 'text-[14px] md:text-[16px] text-[#32302F]',
								}}
								inputProps={{
									id: `${namePrefix}-parental-gross`,
									value: values.parentalAssistanceGrossAmount,
									placeholder: 'Enter Gross Amount',
									onChange: (e) =>
										onChange(
											'parentalAssistanceGrossAmount',
											e.target.value,
										),
									className: `placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border rounded-[15px] bg-[#FBFDFD] ${
										errors?.parentalAssistanceGrossAmount?.length
											? 'border-[#B33044]!'
											: 'border-[#CCCCCC]'
									}`,
								}}
								errors={errors?.parentalAssistanceGrossAmount}
								errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
							/>
							<FloatingSelectField
								labelProps={{
									children: <>Frequency</>,
									className: 'text-[14px] md:text-[16px] text-[#32302F]',
								}}
								value={values.parentalAssistanceFrequency}
								onChange={(v) => onChange('parentalAssistanceFrequency', v)}
								options={ASSISTANCE_FREQUENCIES.map((t) => ({
									label: t,
									value: t,
								}))}
								errors={errors?.parentalAssistanceFrequency}
								errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
								placeholder="Select Frequency"
							/>
						</div>
					</div>
				) : null}
			</div>
			) : null}
		</div>
	)
}
